import { FormattedMessage, useIntl } from 'react-intl'

import { Tooltip } from '@gto/ui/tooltip'
import { Typography } from '@gto/ui/typography'

import { isTempEmail } from '@/utils/is-temp-email'

type PartnerCellProps = {
  displayName: string
  email: string | null | undefined
}

/**
 * Partner identity cell — display name + email (or, for a migration
 * placeholder address, a muted "no email on file" line with an amber dot).
 *
 * Marketing-team rule: a temp/placeholder email is meaningless to them, so we
 * render the FACT ("no real contact email") rather than the noise (the
 * generated `@gtowizard.invalid` address). The amber dot is the same visual
 * vocabulary the rest of the app uses for "needs attention" state, and the
 * tooltip explains why so the cell is not just a coloured pixel.
 */
export const PartnerCell = ({ displayName, email }: PartnerCellProps) => {
  const { formatMessage } = useIntl()
  const temp = isTempEmail(email)

  return (
    <div className="flex min-w-0 items-center">
      <div className="flex min-w-0 flex-col">
        <Typography
          noWrap
          className="font-medium"
          variant="body2"
        >
          {displayName}
        </Typography>
        {temp ? (
          <Tooltip content={formatMessage({ id: 'admin.partners.tempEmail.tooltip' })}>
            <span className="inline-flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block size-3 rounded-full bg-warning-main"
              />
              <Typography
                noWrap
                className="italic"
                color="text.secondary"
                variant="caption"
              >
                <FormattedMessage id="admin.partners.tempEmail.label" />
              </Typography>
            </span>
          </Tooltip>
        ) : email == null || email === '' ? (
          // No email at all — the row would otherwise render nothing under the
          // name. Match the same muted treatment as temp, without the dot.
          <Typography
            noWrap
            color="text.secondary"
            variant="caption"
          >
            <FormattedMessage id="admin.partners.tempEmail.label" />
          </Typography>
        ) : (
          <Typography
            noWrap
            color="text.secondary"
            variant="caption"
          >
            {email}
          </Typography>
        )}
      </div>
    </div>
  )
}
